/**
 * CallToggle - Toggle elegante estilo Koomo para estado de llamada
 * Ubicación: Header del ProspectPanel, junto al nombre
 */

import { useState, useEffect } from 'react'
import { useCallTracking } from '../../hooks/useCallTracking'

export default function CallToggle({ contact }) {
  const { isInCall, getCallForContact, startCall, endCall, getCallDuration } = useCallTracking()
  const [duration, setDuration] = useState('0:00')
  const [isToggling, setIsToggling] = useState(false)

  const activeCall = getCallForContact(contact?.id)
  const inCall = !!activeCall

  // Timer para actualizar duración cada segundo
  useEffect(() => {
    if (!activeCall) {
      setDuration('0:00')
      return
    }

    const updateDuration = () => {
      setDuration(getCallDuration(activeCall.startedAt))
    }

    updateDuration()
    const interval = setInterval(updateDuration, 1000)

    return () => clearInterval(interval)
  }, [activeCall, getCallDuration])

  const handleToggle = async () => {
    if (!contact?.id || isToggling) return

    setIsToggling(true)
    try {
      if (inCall) {
        await endCall(activeCall.id)
      } else {
        await startCall(contact)
      }
    } finally {
      setIsToggling(false)
    }
  }

  if (!contact?.id) return null

  return (
    <div className="flex items-center gap-2">
      {/* Toggle Switch */}
      <button
        onClick={handleToggle}
        disabled={isToggling}
        className={`
          relative w-12 h-6 rounded-full transition-all duration-300 ease-in-out
          focus:outline-none focus:ring-2 focus:ring-offset-2
          ${inCall
            ? 'bg-gradient-to-r from-green-500 to-emerald-600 focus:ring-green-500 shadow-lg shadow-green-200'
            : 'bg-gray-300 hover:bg-gray-400 focus:ring-gray-400'
          }
          ${isToggling ? 'opacity-70 cursor-wait' : 'cursor-pointer'}
        `}
        title={inCall ? 'Terminar llamada' : 'Iniciar llamada'}
      >
        {/* Slider knob */}
        <span
          className={`
            absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md
            transition-transform duration-300 ease-in-out flex items-center justify-center
            ${inCall ? 'translate-x-6' : 'translate-x-0'}
          `}
        >
          {isToggling ? (
            <svg className="animate-spin w-3 h-3 text-gray-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : inCall ? (
            <svg className="w-3 h-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
          ) : (
            <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
          )}
        </span>
      </button>

      {/* Duration Display */}
      {inCall && (
        <div className="flex items-center gap-1.5 px-2 py-1 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
          </span>
          <span className="text-xs font-mono font-semibold text-green-700">{duration}</span>
        </div>
      )}
    </div>
  )
}
